import type { SerializedStyles } from '@emotion/react';
import { css } from '@emotion/react';
import type { IconNode } from 'lucide';
import theme from 'storybookConfig/stories/theme';
import type { IconName } from 'storybookConfig/mixins/icons';
import icons from 'storybookConfig/mixins/icons';

import type { ThemeColor } from 'storybookConfig/stories/theme';

export type MaterialSymbolOptions = {
  /** A Lucide name from `mixins/icons.ts`. */
  name: IconName;
  position?: 'before' | 'after';
  color?: ThemeColor;
  /** 1 fills the shape, for a toggled state such as a favourite. Interior strokes fill too. */
  fill?: number;
  size?: string;
  /**
   * `SerializedStyles` is the result of a Styled `css` literal.
   * `false` allows conditionals, e.g. `disabled && 'opacity: 0.2'`
   */
  additionalStyling?: SerializedStyles | false;
};

const masks = new Map<string, string>();

// The SVG is a mask, so only its shape matters; the colour comes from `background-color`.
const toMask = (node: IconNode, filled: boolean) => {
  const shapes = node
    .map(
      ([tag, attrs]) =>
        `<${tag} ${Object.entries(attrs)
          .map(([key, value]) => `${key}="${value}"`)
          .join(' ')}/>`
    )
    .join('');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="${filled ? 'black' : 'none'}" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">${shapes}</svg>`;

  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

const maskFor = (name: IconName, filled: boolean) => {
  // Legacy .jsx call sites are not type-checked, so an unknown name can still arrive here.
  if (!icons[name]) {
    if (import.meta.env.DEV) console.warn(`materialSymbol: "${name}" is not in mixins/icons.ts`);
    return 'none';
  }

  const key = `${name}:${filled}`;
  if (!masks.has(key)) masks.set(key, toMask(icons[name], filled));

  return masks.get(key);
};

const colorFor = (color?: ThemeColor) => {
  if (!color) return 'currentColor';

  // As with names: a .jsx call site can pass a resolved value or an unknown key. Fall back to the
  // text colour, as the font glyph did, rather than draw an invisible mask.
  if (!(color in theme.color)) {
    if (import.meta.env.DEV) console.warn(`materialSymbol: "${color}" is not a theme colour`);
    return 'currentColor';
  }

  return theme.color[color];
};

/**
 * Draws a Lucide icon (https://lucide.dev/icons) in a `::before` or `::after`, at a 1.5 stroke, in
 * the text colour. The name is kept from the Material Symbols days so call sites did not move.
 */
const materialSymbol = ({
  name,
  position = 'before',
  color,
  fill = 0,
  size = '20px',
  additionalStyling,
}: MaterialSymbolOptions) => css`
  &::${position} {
    content: '';
    display: flex;
    flex-shrink: 0;
    cursor: inherit;
    width: ${size};
    height: ${size};
    background-color: ${colorFor(color)};
    mask: ${maskFor(name, fill === 1)} center / contain no-repeat;

    ${additionalStyling}
  }
`;

export default materialSymbol;
